import { useState } from 'react';
import { useStore } from 'zustand';

import { type Entity } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * How far a turned plane leans off the one it was turned from.
 *
 * ## Why the number lives here and not in the gesture
 *
 * Because a dihedral in a problem is a *stated* number. "Mặt phẳng qua AB hợp với (ABCD) một góc
 * 60°" says sixty, and a figure at fifty-eight is a figure that answers a different question —
 * which is the whole objection `TransformPanel` raises against dragging for a rotation, and it
 * applies with more force here: nothing on the page reports a dihedral, so a reader has no way to
 * notice it drifted.
 *
 * So the tool creates at sixty and this is where sixty becomes forty-five. That is also the shape
 * every other construction parameter in this product has — a solid arrives at a default size and
 * the rail resizes it — and a second pattern would be a second thing to learn.
 *
 * ## Why reverse is a button and not a second field
 *
 * Every angle admits two planes, and half the time the first one a user gets is the empty one:
 * turning the floor of a box about its own edge tips the plane out through the bottom, and there
 * is nothing left to cut. The fix is a sign change, and a button that does it is one click where
 * re-picking the axis in the other order is four.
 *
 * It negates the number rather than setting a separate flag, so the button and the field can
 * never disagree about which of the two planes is on screen. Onshape shipped the flag version and
 * its users have been unable to establish what their angle is measured from for nine years.
 */

type TurnedPlane = Entity & {
  readonly source: Extract<Entity['source'], { op: 'plane-turn' }>;
};

type FreePlane = Entity & {
  readonly source: Extract<Entity['source'], { op: 'plane-free' }>;
};

export interface PlanePanelProps {
  readonly store: EditorStore;
}

/**
 * The angles worth one click.
 *
 * From the exercise book, not invented: after the right angle, thirty, forty-five and sixty are
 * what the problems state, and typing two digits for a number that appears in half the chapter is
 * friction with no purpose.
 */
const COMMON = [30, 45, 60, 90] as const;

const isTurned = (entity: Entity | undefined): entity is TurnedPlane =>
  entity?.source.op === 'plane-turn';

const isFree = (entity: Entity | undefined): entity is FreePlane =>
  entity?.source.op === 'plane-free';

export function PlanePanel({ store }: PlanePanelProps) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const chosen = [...selection].map((id) => document.entities.get(id));

  /*
   * Two kinds of plane carry an angle, and one panel edits both.
   *
   * A **turned** plane leans off another and its sign says which of the two answers is on screen;
   * a **blackboard** plane leans off level and has no other plane to be reversed against. So the
   * fields are the same and the reverse button belongs to only one of them — which is why they
   * are told apart here rather than in the fields below.
   */
  const free = chosen.filter(isFree);
  if (free.length > 0) {
    const first = free[0];
    if (first === undefined) return null;
    const ids = free.map((entity) => entity.id);
    const shared = free.every((entity) => entity.source.tilt === first.source.tilt)
      ? first.source.tilt
      : undefined;

    return (
      <TurnFields
        key={ids.join(',')}
        count={free.length}
        shared={shared}
        onSet={(tilt) => {
          commands.setPlaneFree(ids, { tilt });
        }}
      />
    );
  }

  const planes = chosen.filter(isTurned);

  const first = planes[0];
  if (first === undefined) return null;

  const ids = planes.map((entity) => entity.id);
  const shared = planes.every((entity) => entity.source.degrees === first.source.degrees)
    ? first.source.degrees
    : undefined;

  /*
   * Keyed on the selection, so React discards a half-typed number when the user picks a different
   * plane — the framework's own way of saying "this state belongs to that thing". Clearing it from
   * an effect would work, re-render twice, and fight the lint rule that exists to stop that.
   */
  return (
    <TurnFields
      key={ids.join(',')}
      count={planes.length}
      shared={shared}
      onSet={(degrees) => {
        commands.setPlaneTurn(ids, { degrees });
      }}
      onReverse={() => {
        commands.setPlaneTurn(ids, { reverse: true });
      }}
    />
  );
}

interface TurnFieldsProps {
  readonly count: number;
  readonly shared: number | undefined;
  readonly onSet: (degrees: number) => void;
  /** Absent for a blackboard plane, which leans off level and has nothing to be reversed against. */
  readonly onReverse?: (() => void) | undefined;
}

function TurnFields({ count, shared, onSet, onReverse }: TurnFieldsProps) {
  /*
   * The box holds text, not a number, so a half-typed angle is allowed to exist. Binding it
   * straight to `degrees` would mean an empty box is zero and the section collapses onto its own
   * base between two keystrokes — and a minus sign on its own is not a number at all.
   */
  const [typed, setTyped] = useState('');
  const asked = Number(typed);
  const usable = typed.trim() !== '' && Number.isFinite(asked);

  /*
   * The magnitude, because the sign is *direction* and the buttons are about size. A plane at
   * −60° is sixty degrees off its base, and a panel that showed no preset lit for it would be
   * telling the user their figure is at some other angle than the one they can measure.
   */
  const size = shared === undefined ? undefined : Math.abs(shared);

  return (
    <PanelSection title="Góc nghiêng" {...(count === 1 ? {} : { badge: String(count) })}>
      <div style={{ color: colour.muted, font: '11px/1.4 var(--es-font)' }}>
        {shared === undefined
          ? 'Các mặt phẳng đang nghiêng khác nhau'
          : `Hợp với mặt phẳng gốc một góc ${String(size)}°`}
      </div>

      <div style={{ display: 'flex', gap: 2 }}>
        {COMMON.map((option) => {
          const active = size !== undefined && Math.abs(size - option) < 1e-9;
          return (
            <button
              key={option}
              type="button"
              title={`Nghiêng ${String(option)}° so với mặt phẳng gốc`}
              onClick={() => {
                // Keeps the direction the figure already has: only the size is being changed.
                onSet(shared !== undefined && shared < 0 ? -option : option);
              }}
              className={active ? 'es-btn es-btn--on' : 'es-btn'}
              aria-pressed={active}
              style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
            >
              {option}°
            </button>
          );
        })}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Góc khác</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            aria-label="Số độ"
            value={typed}
            inputMode="numeric"
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            style={boxStyle}
          />
          <span style={{ color: colour.muted }}>°</span>
          <button
            type="button"
            disabled={!usable}
            onClick={() => {
              onSet(asked);
            }}
            className="es-btn es-btn--primary"
            style={{ padding: '4px 10px', fontSize: 11 }}
          >
            Đặt
          </button>
        </div>
      </label>

      {onReverse !== undefined && (
        <>
          <button
            type="button"
            onClick={onReverse}
            className="es-btn"
            style={{ padding: '4px 8px', fontSize: 11 }}
          >
            Đổi chiều
          </button>

          {/*
            The one thing a user cannot work out by looking, because what they see is nothing at
            all. Both directions are the stated angle; only one stays inside the body.
          */}
          <div style={{ color: colour.faint, font: '10px/1.4 var(--es-font)' }}>
            Không thấy thiết diện? Mỗi góc có hai mặt phẳng — một trong hai nằm ngoài khối. Bấm “Đổi
            chiều”.
          </div>
        </>
      )}
    </PanelSection>
  );
}

const boxStyle = {
  width: 48,
  padding: '4px 6px',
  borderRadius: 4,
  border: '1px solid rgba(0,0,0,0.12)',
  font: '11px var(--es-font)',
} as const;
